import { z } from 'zod';

export type CommandContract<
    TType extends string = string,
    TInput extends z.ZodType = z.ZodType,
    TOutput extends z.ZodType = z.ZodType,
> = {
    kind: 'COMMAND';
    type: TType;
    input: TInput;
    output: TOutput;
    schema: z.ZodObject<{ type: z.ZodLiteral<TType>; payload: TInput }>;
};

export type QueryContract<
    TType extends string = string,
    TInput extends z.ZodType = z.ZodType,
    TOutput extends z.ZodType = z.ZodType,
> = {
    kind: 'QUERY';
    type: TType;
    input: TInput;
    output: TOutput;
    schema: z.ZodObject<{ type: z.ZodLiteral<TType>; payload: TInput }>;
};

// Parameterised so a consumer taking "a contract, any contract" can still tie its own schema types
// to it — see `Operation`, which takes its schemas from the contract when they are not passed.
export type OperationContract<
    TType extends string = string,
    TInput extends z.ZodType = z.ZodType,
    TOutput extends z.ZodType = z.ZodType,
> = CommandContract<TType, TInput, TOutput> | QueryContract<TType, TInput, TOutput>;

export function defineCommand<
    TType extends string,
    TInput extends z.ZodType,
    TOutput extends z.ZodType = z.ZodVoid,
>(type: TType, input: TInput, output?: TOutput): CommandContract<TType, TInput, TOutput> {
    return {
        kind: 'COMMAND',
        type,
        input,
        output: (output ?? z.void()) as TOutput,
        schema: z.object({ type: z.literal(type), payload: input }),
    };
}

export function defineQuery<
    TType extends string,
    TInput extends z.ZodType,
    TOutput extends z.ZodType,
>(type: TType, input: TInput, output: TOutput): QueryContract<TType, TInput, TOutput> {
    return {
        kind: 'QUERY',
        type,
        input,
        output,
        schema: z.object({ type: z.literal(type), payload: input }),
    };
}
